import { P2GGrid, useP2GMediaQueryDown } from '@parcel2go/core-ui';
import { Entry, useAnalytics, useContent } from '@parcel2go/npm-p2g-app';
import { memo, ReactNode, Suspense, useMemo } from 'react';
import { BasketQuote, QuoteParcel } from '~/lib/types/basket-types';
import { Promotion } from './promotion';
import { quoteToAnalyticsEvent } from '~/lib/quotes';

const MemorizedPromotion = memo(Promotion);

type PromotionWrapper = {
  id: string;
  title: string;
  collectionCountryCode: string[];
  deliveryCountryCode: string[];
  minWeight: number;
  servicePromotionsDesktop: Entry<PromotionContent>[];
  servicePromotionsMobile: Entry<PromotionContent>[];
};

type PromotionContent = {
  courierName: string;
  features: string[];
  fillColour: string;
  serviceSlug: string;
  strapLine: string;
  image: Entry<PromotionImage>;
};

type PromotionImage = {
  file: {
    url: string;
  };
};

export type CmsPromotion = {
  title: string;
  courierName: string;
  features: string[];
  fillColour: string;
  serviceSlug: string;
  strapLine: string;
  image: string;
  incVat?: boolean;
  quote: BasketQuote | undefined;
};

function QuotePromotions({
  origin,
  destination,
  parcels,
  quotes,
  incVat,
  onClick,
  wrapper
}: {
  origin: string | undefined;
  destination: string | undefined;
  parcels?: QuoteParcel[];
  quotes: BasketQuote[];
  incVat: boolean;
  wrapper: (x: ReactNode) => ReactNode;
  onClick: (index: number, promotion: CmsPromotion, extras: string[]) => void;
}) {
  const content = useContent();
  const isSmall = useP2GMediaQueryDown('sm');
  const { track } = useAnalytics();

  const promotions: CmsPromotion[] = useMemo(() => {
    try {
      const weight = parcels?.reduce((acc, parcel) => acc + parcel.weight, 0);

      if (!content?.state?.data || !origin || !destination || !weight) {
        return [];
      }
      const cmsContent = content.state.data as Entry<PromotionWrapper>[];

      if (!Array.isArray(cmsContent) || cmsContent.length === 0) {
        return [];
      }

      const matchingContent = cmsContent.filter(
        (item) =>
          item.sys?.contentType?.sys?.id === 'component-quote-result-promotion' &&
          item?.fields?.collectionCountryCode?.includes(origin) &&
          item?.fields?.deliveryCountryCode?.includes(destination) &&
          (item.fields?.minWeight == undefined || weight >= item?.fields?.minWeight)
      );

      const bestMatch = matchingContent[0];

      if (!bestMatch) {
        return [];
      }

      const promotions = isSmall ? bestMatch.fields.servicePromotionsMobile : bestMatch.fields.servicePromotionsDesktop;

      const items = promotions
        .map((promo) => ({
          title: bestMatch.fields.title,
          courierName: promo.fields.courierName,
          features: promo.fields.features,
          fillColour: promo.fields.fillColour,
          serviceSlug: promo.fields.serviceSlug,
          strapLine: promo.fields.strapLine,
          image: promo.fields.image.fields.file.url,
          quote: quotes.find((quote) => quote.service.slug === promo.fields.serviceSlug)
        }))
        .filter((promo) => promo.quote);

      if (items.length > 0) {
        track({
          type: 'view_promotion',
          payload: {
            promotion_name: bestMatch.fields.title,
            items: items.map((p, index) => {
              return { index: index, creative_name: p.courierName, ...quoteToAnalyticsEvent(p.quote!) };
            })
          },
          persist: false
        });
      }

      return items;
    } catch (e) {
      console.error(e);
      return [];
    }
  }, [content, origin, destination, parcels, isSmall, quotes]);

  if (!promotions.length) {
    return null;
  }

  return wrapper(
    <P2GGrid
      container
      spacing={2}
      flexWrap={isSmall ? 'wrap' : 'nowrap'}
      direction={isSmall ? 'column' : 'row'}
      justify="center"
    >
      {promotions.map((promotion, index) => (
        <P2GGrid item key={promotion.serviceSlug} xs={12} sm={4}>
          <Suspense>
            <MemorizedPromotion incVat={incVat} promotion={promotion} onClick={onClick} index={index} />
          </Suspense>
        </P2GGrid>
      ))}
    </P2GGrid>
  );
}

export default QuotePromotions;
